import { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, Plus, Trash2, X } from 'lucide-react';
import {
    useCreatePlanFeatureMutation,
    useDeletePlanFeatureMutation,
    useGetAdminPlansQuery,
    useGetPlanFeaturesQuery,
    useGetPricingQuery,
    useUpdatePlanFeatureMutation,
} from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { AdminPlanFeature, CreatePlanFeatureRequest } from '@/types';

function formatNaira(value: number) {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        value || 0,
    );
}

// Turn a label into the slug the API wants, so an admin never has to think
// about the id format.
function slugify(label: string) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
}

/**
 * The priced feature catalog. This is where plan prices actually come from: a
 * plan costs the sum of its features, so re-pricing a feature here re-prices
 * every plan that includes it, live.
 */
export default function PlanFeaturesManager() {
    const { data: features, isLoading, error: loadError } = useGetPlanFeaturesQuery();
    const { data: plans = [] } = useGetAdminPlansQuery();
    const { data: pricing } = useGetPricingQuery();
    const discountPercent = pricing?.wholePlanDiscountPercent ?? 15;
    const [createFeature, { isLoading: isCreating }] = useCreatePlanFeatureMutation();

    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const ordered = useMemo(() => [...(features ?? [])].sort((a, b) => a.position - b.position), [features]);

    // What each plan currently costs, and which plans a feature sits on. Both
    // fall straight out of the plan compositions.
    const planTotals = useMemo(() => {
        const byId = new Map((features ?? []).map((f) => [f.id, f]));
        return plans.map((plan) => {
            const separate = (plan.featureIds ?? []).reduce((sum, id) => sum + (byId.get(id)?.price ?? 0), 0);
            return {
                id: plan.id,
                name: plan.name,
                separate,
                // What the plan actually sells for: the features, discounted.
                price: Math.round(separate * (1 - discountPercent / 100)),
            };
        });
    }, [plans, features, discountPercent]);

    const plansUsing = (featureId: string) =>
        plans.filter((p) => (p.featureIds ?? []).includes(featureId)).map((p) => p.name);

    const handleCreate = async (draft: CreatePlanFeatureRequest) => {
        setError('');
        setSuccess('');
        try {
            await createFeature(draft).unwrap();
            setIsAdding(false);
            setSuccess(`"${draft.label}" added to the catalog. Assign it to a plan on the Plans screen.`);
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to create the feature.'));
        }
    };

    return (
        <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-bold text-gray-900">Feature prices</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        These are standalone prices: what each check costs bought on its own. A plan sells for its
                        features less the whole plan discount, so this is where plan prices are set. Re-pricing a
                        feature re-prices every plan that includes it.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setIsAdding((v) => !v)}
                    className="shrink-0 bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-1.5"
                >
                    {isAdding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    {isAdding ? 'Cancel' : 'New feature'}
                </button>
            </div>

            {/* What the plans currently come to — the whole point of the screen. */}
            {planTotals.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {planTotals.map((plan) => (
                        <span
                            key={plan.id}
                            className="inline-flex items-baseline gap-1.5 rounded-lg bg-gray-50 px-3 py-1.5 text-xs"
                        >
                            <span className="text-gray-500">{plan.name}</span>
                            <span className="font-bold text-gray-900 tabular-nums">{formatNaira(plan.price)}</span>
                            <span className="text-gray-400 line-through tabular-nums">
                                {formatNaira(plan.separate)}
                            </span>
                        </span>
                    ))}
                </div>
            )}

            {error && (
                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    {error}
                </div>
            )}
            {success && (
                <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    <Check className="w-4 h-4 shrink-0" />
                    {success}
                </div>
            )}

            {isAdding && <FeatureForm isSaving={isCreating} onSubmit={handleCreate} />}

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin" />
                </div>
            ) : loadError ? (
                <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Failed to load the feature catalog.
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {ordered.map((feature, index) => (
                        <FeatureRow
                            key={feature.id}
                            feature={feature}
                            usedBy={plansUsing(feature.id)}
                            isFirst={index === 0}
                            isLast={index === ordered.length - 1}
                            onError={setError}
                            onSuccess={setSuccess}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function FeatureRow({
    feature,
    usedBy,
    isFirst,
    isLast,
    onError,
    onSuccess,
}: {
    feature: AdminPlanFeature;
    usedBy: string[];
    isFirst: boolean;
    isLast: boolean;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
}) {
    const [updateFeature, { isLoading: isSaving }] = useUpdatePlanFeatureMutation();
    const [deleteFeature, { isLoading: isDeleting }] = useDeletePlanFeatureMutation();
    const [price, setPrice] = useState(String(feature.price));
    const [isEditing, setIsEditing] = useState(false);

    const priceNum = Number(price);
    const priceValid = price.trim() !== '' && Number.isInteger(priceNum) && priceNum >= 0;
    const changed = priceValid && priceNum !== feature.price;

    const handleSavePrice = async () => {
        onError('');
        if (!priceValid) {
            onError('Price must be a whole number of ₦0 or more.');
            return;
        }
        try {
            await updateFeature({ id: feature.id, data: { price: priceNum } }).unwrap();
            setIsEditing(false);
            onSuccess(
                usedBy.length > 0
                    ? `"${feature.label}" re-priced. ${usedBy.join(', ')} moved by ${formatNaira(Math.abs(priceNum - feature.price))}.`
                    : `"${feature.label}" re-priced.`,
            );
        } catch (err) {
            onError(parseErrorMessage(err, 'Failed to update the feature.'));
        }
    };

    const handleMove = async (direction: -1 | 1) => {
        onError('');
        try {
            await updateFeature({
                id: feature.id,
                data: { position: Math.max(0, feature.position + direction) },
            }).unwrap();
        } catch (err) {
            onError(parseErrorMessage(err, 'Failed to reorder.'));
        }
    };

    const handleDelete = async () => {
        onError('');
        onSuccess('');
        try {
            await deleteFeature(feature.id).unwrap();
            onSuccess(`"${feature.label}" deleted.`);
        } catch (err) {
            // Refused by the API while any plan still includes it.
            onError(parseErrorMessage(err, 'Failed to delete the feature.'));
        }
    };

    return (
        <div
            className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                feature.isActive ? 'border-gray-100' : 'border-gray-100 bg-gray-50 opacity-70'
            }`}
        >
            <div className="flex flex-col gap-0.5 shrink-0">
                <button
                    type="button"
                    onClick={() => handleMove(-1)}
                    disabled={isFirst || isSaving}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    aria-label="Move up"
                >
                    <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                    type="button"
                    onClick={() => handleMove(1)}
                    disabled={isLast || isSaving}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300"
                    aria-label="Move down"
                >
                    <ChevronDown className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{feature.label}</p>
                <p className="text-[11px] text-gray-400 truncate">
                    {usedBy.length > 0 ? `On ${usedBy.join(', ')}` : 'Not on any plan yet'}
                    {!feature.isActive && ' · hidden'}
                </p>
            </div>

            {isEditing ? (
                <div className="flex items-center gap-1.5 shrink-0">
                    <div className="relative w-28">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">₦</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoFocus
                            value={price}
                            onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
                            className="w-full border border-gray-200 rounded-lg pl-5 pr-2 py-1.5 text-sm focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleSavePrice}
                        disabled={!changed || isSaving}
                        className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium"
                    >
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setPrice(String(feature.price));
                            setIsEditing(false);
                        }}
                        className="text-gray-300 hover:text-gray-600 p-1.5"
                        aria-label="Cancel"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="text-sm font-bold text-[#FF6B35] tabular-nums hover:underline"
                    >
                        {formatNaira(feature.price)}
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="text-gray-300 hover:text-red-500 disabled:opacity-50 p-1.5"
                        aria-label={`Delete ${feature.label}`}
                    >
                        {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                </div>
            )}
        </div>
    );
}

function FeatureForm({
    isSaving,
    onSubmit,
}: {
    isSaving: boolean;
    onSubmit: (draft: CreatePlanFeatureRequest) => void | Promise<void>;
}) {
    const [label, setLabel] = useState('');
    const [id, setId] = useState('');
    const [idTouched, setIdTouched] = useState(false);
    const [price, setPrice] = useState('');

    const priceNum = Number(price);
    const priceValid = price.trim() !== '' && Number.isInteger(priceNum) && priceNum >= 0;
    const canSave = label.trim() !== '' && id.trim() !== '' && priceValid;

    const handleLabel = (value: string) => {
        setLabel(value);
        // Keep the slug in step with the label until the admin edits it.
        if (!idTouched) setId(slugify(value));
    };

    return (
        <div className="rounded-xl border border-[#FF6B35]/30 bg-orange-50/40 p-4 flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-800">Bullet text</label>
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => handleLabel(e.target.value)}
                        placeholder="Drug Screening"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]"
                    />
                    <span className="text-[11px] text-gray-400">Shown as a bullet on the plan card</span>
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-800">Price</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">₦</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={price}
                            onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
                            className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>
                    <span className="text-[11px] text-gray-400">
                        {priceValid ? formatNaira(priceNum) : 'Whole naira, no kobo'}
                    </span>
                </div>
            </div>

            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-800">Id</label>
                <input
                    type="text"
                    value={id}
                    onChange={(e) => {
                        setIdTouched(true);
                        setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                    }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#FF6B35]"
                />
                <span className="text-[11px] text-gray-400">
                    The checkout key. Lower-case, no spaces. Cannot be changed later.
                </span>
            </div>

            <p className="text-[11px] text-gray-500">
                A new feature costs nothing until you put it on a plan. Assign it on the Plans screen, and that plan’s
                price rises by exactly this amount.
            </p>

            <button
                type="button"
                disabled={!canSave || isSaving}
                onClick={() => onSubmit({ id: id.trim(), label: label.trim(), price: priceNum })}
                className="self-start bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-sm font-medium flex items-center gap-2"
            >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                Create feature
            </button>
        </div>
    );
}
